import createClient from 'openapi-fetch';
import { acquireApiToken } from '../auth/msal';
import { ApiError, NetworkError, parseIssues } from './errors';
import type { paths } from './schema';

export const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? '';

/** Typed client generated from /contract/openapi.json — regenerated + drift-checked in CI. */
export const api = createClient<paths>({ baseUrl: apiBaseUrl });

api.use({
  async onRequest({ request }) {
    const token = await acquireApiToken();
    if (token) request.headers.set('Authorization', `Bearer ${token}`);
    return request;
  },
});

/**
 * Untyped escape hatch for the generic CRUD pages, whose paths are assembled at runtime. Same
 * base URL and auth; purpose-built pages should prefer the typed `api` client above.
 *
 * Failures always arrive as `ApiError` or `NetworkError` so that callers never have to inspect a
 * status code or a server message to decide what to tell a person — see api/errors.ts.
 */
export async function rawApi<T = unknown>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const token = await acquireApiToken();

  let res: Response;
  try {
    res = await fetch(`${apiBaseUrl}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    // fetch only rejects when the request never completed — offline, DNS, CORS. Without this
    // branch the user sees the browser's own "Failed to fetch".
    throw new NetworkError(cause);
  }

  if (!res.ok) {
    let code = 'unknown';
    let message = '';
    /**
     * Kept whole, not just its two known fields. A refusal that answers about ONE thing inside a
     * request carrying many — the entry a whole-period timesheet save was refused over — has
     * nowhere else to say which, and a screen told only "refused" would have to throw away
     * everything typed. See `ApiError.body`.
     */
    let payload: unknown;
    try {
      payload = await res.json();
      const named = payload as { message?: string; error?: string };
      code = named.error ?? code;
      message = named.message ?? '';
    } catch {
      // A non-JSON body means we are past the API (a proxy or edge error page); the status is
      // all the signal there is.
    }
    throw new ApiError(res.status, code, message, code === 'validation' ? parseIssues(message) : [], payload);
  }

  // 204 and other empty successes have no body to parse.
  if (res.status === 204 || res.headers.get('content-length') === '0') return undefined as T;
  return (await res.json()) as T;
}

/**
 * The same request, for the endpoints that answer a file rather than JSON.
 *
 * A stored fiscal document is the only one of these today, and it is deliberately behind the same
 * bearer as everything else — there is no public link and no presigned URL for a document with a
 * CAI on it. Failures still arrive as `ApiError`, because the body of a failure is JSON even when
 * the body of a success is not, and a caller should not have to know that.
 */
export async function rawApiFile(path: string): Promise<Blob> {
  const token = await acquireApiToken();

  let res: Response;
  try {
    res = await fetch(`${apiBaseUrl}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  if (!res.ok) {
    let code = 'unknown';
    let message = '';
    try {
      const payload = (await res.json()) as { message?: string; error?: string };
      code = payload.error ?? code;
      message = payload.message ?? '';
    } catch {
      // Past the API — a proxy or an edge error page. The status is all the signal there is.
    }
    throw new ApiError(res.status, code, message, []);
  }

  return await res.blob();
}
